/* eslint-disable consistent-return */
/* eslint-disable array-callback-return */

import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';

export type AppDispatch = ThunkDispatch<any, any, AnyAction>;

export const capitalizeFirstLetter = (text: string) => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const isNullOrUndefinedOrEmpty = (value: any): boolean => {
  return value === null || value === undefined || value === '';
};

export const getRandomColor = () => {
  const letters = '0123456789ABCDEF';
  let color = '#';
  for (let index = 0; index < 6; index += 1) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
};

export const getClosestToTargetItemCountDimension = (data: any) => {
  const closestItem = data.reduce((closest: any, item: any) => {
    if (item.dim_item_count <= 15 && (!closest || item.dim_item_count > closest.dim_item_count)) {
      return item;
    }
    return closest;
  }, null);
  return closestItem ? closestItem.id : 'Total';
};

export const indicatorfiltersName = (filtersData: any, blockDimensions: any) => {
  const selectedCheckboxes = filtersData?.map((item: any) => {
    const key = Object.keys(item)[0];
    const index = blockDimensions.findIndex((block: any) => block?.name === key);
    if (item[key]?.length < 1) return null;
    if (item[key]?.length === blockDimensions[index]?.items?.length) return `All ${key}`;
    if (item[key]?.length === 1) return item[key][0];
    if (item[key]?.length < 3) return item[key]?.join(', ');
    if (item[key]?.length >= 3)
      return `${item[key]?.slice(0, 2).join(', ')}...(${item[key].length - 2})`;
  });

  return selectedCheckboxes;
};

type DimensionFilterEntry = Record<string, string[]>;

interface BlockDimensionsShape {
  dimensions?: Array<{ name: string; items?: string[] }>;
}

/**
 * Returns true when at least one dimension has a strict subset of items selected
 * (i.e. an active filter is applied, not "show all").
 */
export const hasActiveDimensionFilters = (
  filters: DimensionFilterEntry[] | null | undefined,
  blockDimensions: BlockDimensionsShape | null | undefined,
): boolean => {
  if (!filters?.length || !blockDimensions?.dimensions?.length) return false;

  return filters.some((dim) => {
    const [name, selected] = Object.entries(dim ?? {})[0] ?? [];
    if (!name || !Array.isArray(selected) || selected.length === 0) return false;

    const meta = blockDimensions.dimensions?.find((dimension) => dimension.name === name);
    const totalItems = meta?.items?.length ?? 0;
    return totalItems > 0 && selected.length < totalItems;
  });
};

/**
 * Returns the plan-page dimension filter payload to use for table/chart requests.
 * Prefers applied Filters state, then falls back to selectedBlockDimension.
 */
export const getActivePlanFilterSource = (
  appliedFilters: DimensionFilterEntry[] | null | undefined,
  selectedBlockDimension: DimensionFilterEntry[] | null | undefined,
  blockDimensions: BlockDimensionsShape | null | undefined,
): DimensionFilterEntry[] | null => {
  if (hasActiveDimensionFilters(appliedFilters, blockDimensions) && appliedFilters?.length) {
    return appliedFilters;
  }
  if (hasActiveDimensionFilters(selectedBlockDimension, blockDimensions)) {
    return selectedBlockDimension ?? null;
  }
  return null;
};

/** JSON string for API dim_filter params; must stay in sync with plan page fetch helpers. */
export const serializeDimFilter = (filters: unknown) => {
  if (!Array.isArray(filters)) return '[]';
  return JSON.stringify(filters);
};

export const getTableFilterPayload = (
  selectedDimensions: DimensionFilterEntry,
  blockDimensions: { id?: number | string; items?: string[] },
) => {
  const selectedDimItems = Object.values(selectedDimensions)[0];
  const filterItems = selectedDimItems.map((item: string) => {
    return { name: item, status: true, search: true };
  });

  const dimId = blockDimensions?.id;
  const dimensionItems = blockDimensions?.items?.map((item: string) => {
    if (selectedDimItems.includes(item)) return { name: item, status: true, search: true };
    return { name: item, status: false, search: true };
  });

  return {
    tableItems: { dimId, filterItems },
    dimensionItems: { id: dimId, resultedItems: dimensionItems },
  };
};

export const hasStartDate = (text: string) =>
  text === 'Start Date' ||
  text === 'start date' ||
  text === 'Start date' ||
  text === 'start Date' ||
  text === 'start' ||
  text === 'Start';

export const hasEndDate = (text: string) =>
  text === 'End Date' ||
  text === 'end date' ||
  text === 'End date' ||
  text === 'end Date' ||
  text === 'End' ||
  text === 'end';

export const hasStartAndEndDate = (text: string) => hasStartDate(text) && hasEndDate(text);
